import { useState, useEffect } from "react"
import { OrderDirection, Token } from "utils/types"
import Loading from "components/Loading"
import MetadataAwareTokenView from "components/MetadataAwareTokenView"
import useWindowSize from "hooks/useWindowSize"
import useOwnedTokens from "hooks/useOwnedTokens"
import useCountOwnedTokens from "hooks/useCountOwnedTokens"
import { getContractChainId } from "utils/contractInfoHelper"

const OWNED_TOKENS_PER_PAGE = 3

interface Props {
  contractAddress: string
  projectId: string
  walletAddress: string
  aspectRatio: number
}

const OwnedTokens = ({ contractAddress, projectId, walletAddress, aspectRatio }: Props) => {
  const windowSize = useWindowSize()
  const [currentPage, setCurrentPage] = useState(0)
  const [countOwnedTokens, setCountOwnedTokens] = useState(0)
  const skip = currentPage * OWNED_TOKENS_PER_PAGE
  const { loading, error, data } = useOwnedTokens(projectId, walletAddress, {
    first: OWNED_TOKENS_PER_PAGE,
    skip,
    orderDirection: OrderDirection.ASC,
  })
  const countResponse = useCountOwnedTokens(projectId, walletAddress)
  const chainId = getContractChainId(contractAddress)

  useEffect(() => {
    setCountOwnedTokens(countResponse?.data?.tokens?.length ?? 0)
  }, [countResponse])

  const colWidth =
    windowSize.width >= 1200
      ? (1200 - 96) / 3
      : windowSize.width >= 768
      ? (windowSize.width - 96) / 3
      : (windowSize.width - 60) / 2

  if (loading) return <Loading />
  if (error) return <p className="text-sm text-red-500">Error loading tokens.</p>
  if (!data?.tokens?.length) return <p className="text-sm text-gallery-muted">No tokens.</p>

  const totalPages = Math.ceil(countOwnedTokens / OWNED_TOKENS_PER_PAGE)

  return (
    <div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {data.tokens.map((token: Token) => (
          <a key={token.tokenId} href={`/token/${contractAddress}/${token.tokenId}`} className="block group">
            <div className="group-hover:opacity-90 transition-opacity duration-300">
              <MetadataAwareTokenView
                contractAddress={contractAddress}
                tokenId={token.tokenId}
                chainId={chainId}
                fallbackAspectRatio={aspectRatio}
                width={Math.floor(colWidth)}
              />
            </div>
            <p className="mt-2 gallery-label text-gallery-muted">#{token.invocation.toString()}</p>
          </a>
        ))}
      </div>

      {totalPages > 1 && (
        <div className="flex justify-center items-center gap-4 mt-8 mb-4">
          <button
            disabled={currentPage === 0}
            onClick={() => setCurrentPage(currentPage - 1)}
            className="gallery-label disabled:opacity-30 hover:text-gallery-text transition-colors"
          >
            ← Prev
          </button>
          <span className="gallery-label text-gallery-muted">{currentPage + 1} / {totalPages}</span>
          <button
            disabled={currentPage === totalPages - 1}
            onClick={() => setCurrentPage(currentPage + 1)}
            className="gallery-label disabled:opacity-30 hover:text-gallery-text transition-colors"
          >
            Next →
          </button>
        </div>
      )}
    </div>
  )
}

export default OwnedTokens
